import { useState, type FormEvent } from "react";
import { Brand } from "../../components/Brand";
import { EyeIcon, LockIcon, SafetyIcon } from "../../components/Icons";
import { readableError } from "../../lib/errors";

interface LoginScreenProps {
  onLogin: (email: string, password: string, remember: boolean) => Promise<void>;
  onSetup: () => void;
  onRecover: () => void;
  setupRequired: boolean;
  setupAvailable: boolean;
}

export function LoginScreen({
  onLogin,
  onSetup,
  onRecover,
  setupRequired,
  setupAvailable
}: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await onLogin(email, password, remember);
    } catch (cause) {
      setError(readableError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-screen__form">
        <Brand />
        <div className="auth-screen__content">
          <h1>Enter your<br />private space</h1>
          <p className="auth-screen__lede">
            Two accounts. One shared session.<br />No QIUI login required.
          </p>

          <form onSubmit={submit} className="auth-form">
            <label>
              <span>Email</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                required
              />
            </label>
            <label>
              <span>Password</span>
              <span className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="icon-button password-field__toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <label className="check-row">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              <span>Keep me signed in on this device</span>
            </label>

            {error && <p className="form-error" role="alert">{error}</p>}

            <button className="button button--primary button--wide" disabled={busy}>
              {busy ? "SIGNING IN…" : "SIGN IN"} <span aria-hidden="true">→</span>
            </button>
            <button type="button" className="text-button" onClick={onRecover}>
              Forgot password?
            </button>
            <div className="or-divider"><span>OR</span></div>
            <button
              type="button"
              className="text-button text-button--setup"
              onClick={onSetup}
              disabled={!setupRequired || !setupAvailable}
            >
              <span aria-hidden="true">▣</span>
              {!setupRequired
                ? "Server setup is complete"
                : setupAvailable
                  ? "Set up this server"
                  : "Private setup link required"}
              <span aria-hidden="true">›</span>
            </button>
          </form>
        </div>

        <div className="auth-footer">
          <span className="status-dot" /> <LockIcon />
          Self-hosted <b>•</b> Server-managed session <b>•</b> Role assigned by the server
        </div>
      </div>

      <aside className="auth-screen__aside">
        <div className="orbit-art" aria-hidden="true">
          <i /><i /><i /><b /><b /><b />
        </div>
        <div className="privacy-panel">
          <div>
            <SafetyIcon />
            <span>
              <strong>Your role is protected</strong>
              Your account opens only the controls assigned to you. Roles cannot
              be changed from this screen.
            </span>
          </div>
          <div>
            <LockIcon className="connected-color" />
            <span>
              <strong className="connected-color">Private by design</strong>
              Accounts and history stay on this server. No QIUI account is used.
            </span>
          </div>
        </div>
      </aside>
    </main>
  );
}
